import { z } from "zod";
import { catalogueLocale } from "../catalog/vocabulary";
import { planFinding, planItemView, slotCandidate } from "./plan";
import { sessionReadiness } from "./workout";
/**
 * `POST /me/quick-workout?locale=pt` — "Treino rápido" (`D249`): *"I have 30
 * minutes, what do I do today?"*
 *
 * ── A PROPOSAL, NEVER A WRITE ───────────────────────────────────────────────
 * The engine composes one session for today from the minutes, the gym's
 * equipment, the muscle readiness model (`D203`), the check-in (`D148`), the
 * person's exclusions, preferences and injuries (`D146`) and the eligibility
 * rule (`D227`), and answers it. NOTHING is stored: not a plan, not a draft,
 * not a session. Starting it is the ordinary `POST /me/sessions` with
 * `planSessionId: null` — an unplanned session, which is what the module has
 * always called a session nobody planned. That is why a quick workout never
 * moves the plan: there is no plan session to advance (`D249`).
 *
 * POST rather than GET only because the request has a body; it is as safe to
 * repeat as a read, and the same request answers the same session.
 */
/** The four sizes offered as chips; any whole minute in range is accepted. */
export const QUICK_WORKOUT_MINUTES = Object.freeze([20, 30, 45, 60]);
/** The shortest session the engine will compose — one exercise and a warm-up. */
export const QUICK_WORKOUT_MIN_MINUTES = 10;
/** The longest — two hours is a session nobody calls quick. */
export const QUICK_WORKOUT_MAX_MINUTES = 120;
/**
 * What the session is about. `muscle` names one of the twelve training
 * muscles by id (`muscleId`); the other three name a region of the body.
 * Held equal to the module's `QUICK_FOCI` by `contract-vocabulary.test.ts`.
 */
export const QUICK_WORKOUT_FOCI = Object.freeze([
    "full_body",
    "upper",
    "lower",
    "muscle",
]);
/** How many swaps each line carries — what the picker offers, offline. */
export const MAX_QUICK_ALTERNATIVES = 6;
export const quickWorkoutQuery = z.object({
    /** The names on the wire. English when absent. */
    locale: catalogueLocale.optional(),
});
export const quickWorkoutInput = z
    .strictObject({
    /** Absent is `full_body`. */
    focus: z.enum(QUICK_WORKOUT_FOCI).optional(),
    /**
     * One of the person's saved gyms (`D202`): its equipment is what the
     * session is composed from. Absent is the equipment in effect — the gym
     * in use, or the checklist when there is none. Composing reads the gym
     * and never switches to it; the switch is its own write (`gym.use`).
     */
    gymId: z.string().min(1).max(64).optional(),
    minutes: z
        .int()
        .min(QUICK_WORKOUT_MIN_MINUTES)
        .max(QUICK_WORKOUT_MAX_MINUTES),
    /** With `focus: "muscle"` and only then. */
    muscleId: z.int().positive().optional(),
    /** Today's check-in (`D148`); absent is a skip, which cuts nothing. */
    readiness: sessionReadiness.optional(),
})
    .superRefine((value, context) => {
    if (value.focus === "muscle" && value.muscleId === undefined) {
        context.addIssue({
            code: "custom",
            message: "a muscle focus names its muscle",
            path: ["muscleId"],
        });
    }
    if (value.focus !== "muscle" && value.muscleId !== undefined) {
        context.addIssue({
            code: "custom",
            message: "muscleId is only for focus: muscle",
            path: ["muscleId"],
        });
    }
});
/**
 * One line of the session: a `planItemView`, so the workout runs it with the
 * code that runs a plan's day, plus what only a quick workout carries.
 *
 * `itemId` and `sessionId` are NOT plan rows: `itemId` is `quick-<n>` and
 * `sessionId` is {@link QUICK_WORKOUT_SESSION_ID}. No plan route accepts them;
 * the swap picker reads `alternatives` instead of the plan's feed.
 */
export const quickWorkoutItem = planItemView.extend({
    /** The same pattern's next eligible candidates, best first — the swaps
     *  "só hoje" offers without a request (`D181`, `D158`). */
    alternatives: z.array(slotCandidate).max(MAX_QUICK_ALTERNATIVES),
    /** Where the load came from: the person's own history (`e1RM` at the
     *  target reps and RIR, never above the last working load), or `none` —
     *  the first-time rule, a load chosen by feel (`D103`). */
    loadBasis: z.enum(["history", "none"]),
    /** The primary muscle's readiness, 0..1 (`D203`); 1 when the last seven
     *  days did not touch it. */
    readiness: z.number().min(0).max(1),
    /** Reps to leave in reserve on every working set: the protocol's floor,
     *  one more after a poor night or under a conservative start. */
    targetRir: z.int().min(0).max(6),
});
/** The `sessionId` every quick item carries — no plan session has it. */
export const QUICK_WORKOUT_SESSION_ID = "quick";
export const quickWorkoutOutput = z.object({
    /** The muscles left out because they are still recovering (`D203`),
     *  by id and in the request's locale. */
    avoided: z.array(z.object({
        muscleId: z.int().positive(),
        name: z.string().min(1),
        readiness: z.number().min(0).max(1),
    })),
    /** What the composed session should take, warm-up included. */
    estimatedMinutes: z.int().nonnegative(),
    focus: z.enum(QUICK_WORKOUT_FOCI),
    /** The gym the equipment came from, or `null` for the equipment in effect. */
    gymId: z.string().min(1).nullable(),
    items: z.array(quickWorkoutItem),
    minutes: z.int().positive(),
    /** What the session could not honour, as keys and params (`D212`'s
     *  findings): a short session, a focus muscle still recovering. */
    notices: z.array(planFinding),
    /** The protocol whose numbers the session uses — the plan's, else the
     *  default (`D103`). */
    protocolId: z.string().min(1),
    /** The general warm-up, inside `estimatedMinutes` (`D103`). */
    warmupMinutes: z.int().nonnegative(),
});
//# sourceMappingURL=quick-workout.js.map